import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { useMediaQuery, useTheme, Box, Typography, IconButton,    
  CircularProgress, Tooltip,  
} from '@mui/material';
import { Visibility } from '@mui/icons-material';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { fetchGroupedRosterDetailsByCrewCode } from "state/api";
import { filterCrewMember } from "state/helpers";
import { hasPermission } from 'state/checkPermission';
import DataGridComponent from 'components/DataGridComponent';
import ScrollToTop from 'components/ScrollToTop';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import FilterButton from 'components/FilterButton';
import CrewFilterDialog from 'components/CrewFilterDialog';
import RosterDetailsDialog from 'components/RosterDetailsDialog';
import OTPByCrewList from './OTPByCrewList';


const hiddenColumns = { _id: false, post: false, base: false, trainingQuals: false, quals: false, isActive: false };
const nonMobileHiddenColumns = { _id: false, isActive: false };

const OTPByCrew = () => {
  useAuthCheck();
  const user = useSelector((state) => state.user); 
  const crewRanksData = useSelector((state) => state.crewRanks);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  
  const canView = hasPermission(user?.permissions, "RosterDetails", "view", false);
  
  const { startDate, endDate } = useDateRangeContext();

  const scrollRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState({ isActive: 'ALL' });
  const defaultFilters = {
    isActive: 'ALL',
  };
  const [openRosterDetailsDialog, setOpenRosterDetailsDialog] = useState(false);
  const [selectedCrewCode, setSelectedCrewCode] = useState(null);
  const [selectedCrewName, setSelectedCrewName] = useState(null);

  const [groupedCrewRosters, setGroupedCrewRosters] = useState([]);
  const [otpByCrew, setOtpByCrew] = useState([]);

        
  useEffect(() => {    
    const fetchOTPByCrew = async () => {
      if (!startDate || !endDate) return; 
      
      try {      
        setError(null);
        setLoading(true);

        const url = '/legmembers/otpbycrew';
        const payload = { startDate, endDate };
        const res = await api.post(url, payload);
        
        setOtpByCrew(res.data);
      } catch (error) {
        setError(error)
        toast.error(error?.message || "Failed to fetch OTP by crew!");
      }
      finally {
        setLoading(false);
      }
    }; 
  
    fetchOTPByCrew();
  }, [startDate, endDate]); 

  const filteredData = useMemo(() => {
    const data = otpByCrew;

    if (!Array.isArray(data)) return [];
    if (!filters) return data;
    return data.filter(member => filterCrewMember(member, filters));
  }, [otpByCrew, filters]);

  
  const handleFilterOpen = () => setIsFilterDialogOpen(true);
  const handleFilterClose = () => setIsFilterDialogOpen(false);
  
  const handleFilterApply = (filterValues) => {
    setFilters(filterValues); 
  };
  
  const handleViewClick = useCallback(async (param) => {
    try {
      const crewCode = param.crewCode;
      const crewName = param.crewName;
      setSelectedCrewCode(crewCode);
      setSelectedCrewName(crewName);
      const result = await fetchGroupedRosterDetailsByCrewCode(crewCode, startDate, endDate);
      
      setGroupedCrewRosters(result);
      setOpenRosterDetailsDialog(true);
    } catch (error) {
      setOpenRosterDetailsDialog(false);
    } 
  }, [startDate, endDate]);

  const handleCloseRosterDetailsDialog = () => {
    setGroupedCrewRosters([]);
    setOpenRosterDetailsDialog(false);
  };

  const columns = useMemo(() => [
    {
      field: "no",
      headerName: "No",
      filterable: false,
      sortable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
      ...(() => isMobile ? { width: 60 } : { flex: 1 })(),
    },
    {
      field: "crewCode",
      headerName: "Code",
      ...(() => isMobile ? { width: 80 } : { flex: 1 })(),
    },
    {
      field: "crewName",
      headerName: "CrewName",
      cellClassName: "name-column--cell",
      ...(isMobile ? { width: 280 } : { flex: 4 }),
      renderCell: (params) => {
        const gender = params.row.gender;
        return (
          <Box
            sx={{
              color: gender === 'FM' ? theme.palette.secondary.main : theme.palette.primary.dark,
            }}
          >
            {params.value}
          </Box>
        );
      },
    },
    {
      field: "post",
      headerName: "Post",
      ...(() => isMobile ? { width: 60 } : { flex: 1 })(),
    },
    {
      field: "base",
      headerName: "Base",
      ...(() => isMobile ? { width: 60 } : { flex: 1 })(),
    },
    {
      field: "trainingQuals",
      headerName: "Trainer",
      ...(() => isMobile ? { width: 60 } : { flex: 1 })(),
    },
    {
      field: "quals",
      headerName: "Quals",
      ...(() => isMobile ? { width: 120 } : { flex: 2 })(),
    },
    {
      field: "sectors",
      headerName: "Sectors",
      cellClassName: 'right-align-cell',
      align: 'right',
      ...(() => isMobile ? { width: 90 } : { flex: 1.5 })(),
    },
    {
      field: "depDelayedSectors",
      headerName: "DepDelayedSectors",
      cellClassName: 'right-align-cell',
      align: 'right',
      ...(() => isMobile ? { width: 90 } : { flex: 1.5 })(),
    },
    {
      field: "arrDelayedSectors",
      headerName: "ArrDelayedSectors",
      cellClassName: 'right-align-cell',
      align: 'right',
      ...(() => isMobile ? { width: 90 } : { flex: 1.5 })(),
    },
    {
      field: "depOtp",
      headerName: "DepOTP (%)",
      cellClassName: 'right-align-cell',
      align: 'right',
      valueFormatter: (value) => value > 0 ? parseFloat(value).toFixed(0) : 0,
      ...(() => isMobile ? { width: 90 } : { flex: 1.5 })(),
    },
    {
      field: "arrOtp",
      headerName: "ArrOTP (%)",
      cellClassName: 'right-align-cell',
      align: 'right',
      valueFormatter: (value) => value > 0 ? parseFloat(value).toFixed(0) : 0,
      ...(() => isMobile ? { width: 90 } : { flex: 1.5 })(),
    },
    {
      field: "isActive",
      headerName: "IsActive",
      ...(() => isMobile ? { width: 90 } : { flex: 1.2 })(),
    },
  ], [theme, isMobile]);

  const actionColumn = useMemo(() => {
    const activeCount = [canView].filter(Boolean).length;
    if (activeCount === 0) return [];

    const baseWidth = 60 + activeCount * 25;

    return [
      {
        field: "action",
        headerName: "Action",
        filterable: false,
        sortable: false,
        width: baseWidth,
        renderCell: (params) => (
          <FlexCenter
            gap={1}
            sx={{ width: '100%', height: '100%' }}
          >
            <Tooltip title="View">
              <IconButton
                aria-label="View"
                sx={{
                  color: theme.palette.primary.dark,
                  cursor: 'pointer',
                  '&:hover': {
                    color: theme.palette.green.deep,
                  }
                }}
                onClick={() => {
                  if (!canView) return;
                  handleViewClick(params.row);
                }}
              >
                <Visibility />
              </IconButton>
            </Tooltip>
          </FlexCenter>
        ),
      }
    ];
  }, [theme, canView, handleViewClick]);

  const columnsToShow = [...columns, ...actionColumn];

  const sortedMobileRows = useMemo(() =>
    [...filteredData].sort((a, b) => (b.sectors || 0) - (a.sectors || 0)),
    [filteredData]
  );

  const [columnVisibilityModel, setColumnVisibilityModel] = useState(
    !isDesktop ? hiddenColumns : nonMobileHiddenColumns
  );

  useEffect(() => {
    setColumnVisibilityModel(!isDesktop ? hiddenColumns : nonMobileHiddenColumns);
  }, [isDesktop]);

  const {
    baseOptions,
    postOptions,
    trainingQualOptions,
    isActiveOptions,
  } = useMemo(() => {
    if (!Array.isArray(otpByCrew) || otpByCrew.length === 0) {
      return {
        baseOptions: [],
        postOptions: [],
        trainingQualOptions: [],
        isActiveOptions: [],
      };
    }

    const baseSet = new Set();
    const postSet = new Set();
    const trainingQualSet = new Set();
    const isActiveSet = new Set();

    otpByCrew.forEach(m => {
      if (m.base) baseSet.add(m.base);
      if (m.post) postSet.add(m.post);
      if (m.isActive !== undefined) isActiveSet.add(m.isActive);

      m.trainingQuals?.forEach(q => q && trainingQualSet.add(q));
    });

    const rankMap = new Map(
      (crewRanksData ?? []).map(r => [r.rankCode, r])
    );

    const baseOptions = [...baseSet].sort();

    const postOptions = [...postSet]
      .map(post => rankMap.get(post))
      .filter(Boolean)
      .sort((a, b) => a.rankPriority - b.rankPriority)
      .map(r => r.rankCode);

    const trainingQualOptions = [...trainingQualSet].sort();

    const isActiveOptions = [...isActiveSet].sort();

    return {
      baseOptions,
      postOptions,
      trainingQualOptions,
      isActiveOptions,
    };
  }, [otpByCrew, crewRanksData]);

  
  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header 
          title="OTP by Crew" 
          subtitle={`${loading ? 'Loading...' : 'List of Members'}`}
        />
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}>
            <FilterButton
              filters={filters}
              defaultFilters={defaultFilters}
              onClick={handleFilterOpen}
            />
          </Box>
        )}                    
      </FlexBetween>
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">Error: {error?.message || ''}</Typography>
        </FlexCenter>
      ) : (
        <Box
          display="grid"
          gap={2}
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
        >
          <Box 
            ref={scrollRef} 
            height={LAYOUT_HEIGHT} 
            sx={{ gridColumn: "span 12", overflowY: 'auto' }}
          >
            {isMobile ? (
              <OTPByCrewList
                rows={sortedMobileRows}
                canView={canView}
                onViewClick={handleViewClick}
              />
            ) : (
              <DataGridComponent
                rows={filteredData}
                columns={columnsToShow}
                columnVisibilityModel={columnVisibilityModel}
                setColumnVisibilityModel={setColumnVisibilityModel}
                loading={loading}
                props={{ initialState: { pagination: { paginationModel: { pageSize: 100 } } } }}
              />
            )}

            <ScrollToTop scrollRef={scrollRef} />
          </Box>
        </Box>
      )}

      {isFilterDialogOpen &&
        <CrewFilterDialog
          open={isFilterDialogOpen}
          onClose={handleFilterClose}
          onApply={handleFilterApply}
          postOptions={postOptions}
          baseOptions={baseOptions}
          trainingQualOptions={trainingQualOptions}
          isActiveOptions={isActiveOptions}
          currentFilters={filters}
        />
      }
      
      {openRosterDetailsDialog &&
        <RosterDetailsDialog 
          open={openRosterDetailsDialog}
          onClose={handleCloseRosterDetailsDialog}
          crewCode={selectedCrewCode}
          crewName={selectedCrewName}
          crewRosters={groupedCrewRosters}
        />
      }
    </Box>
  );
};

export default OTPByCrew;
